<template>
  <div :class="{ 'overview-container--loading': loading }" class="overview-container">
    <!-- SHOW ERROR -->
    <span v-if="showErrorTile" style="display: contents">
      <ErrorTile :title="$t('errorTitle')"></ErrorTile>
    </span>

    <!-- SHOW TILE -->
    <span v-else>
      <Card
        v-if="(loading || !resolvedPolicy) && shouldShowPolicy"
        :loadingState="true"
      ></Card>

      <Card
        v-if="!loading && resolvedPolicy"
        :id="'policy.clickable-policy-card.' + policy.policyId"
        :iconClass="shurIconStringResolved(shurContract)"
        :isPolicy="true"
        :isPolicyDetails="isDetailHeader"
        :title="shurFirstTitle(shurContract)"
      >
        <template #title-extra>
          <div v-if="productSubLine">{{ productSubLine }}</div>
          <div v-else v-dompurify-html="secondTitleSanitizer"></div>
        </template>

        <!-- CONTENT-->
        <template #content>
          <GridContent :with-padding="false">
            <template #gridContent>
              <dl class="info">
                <dt class="info__label">{{ $t('policyTile.policyNumber') }}</dt>
                <dd class="info__value">{{ resolvedPolicy.policyNumber }}</dd>
              </dl>
              <dl>
                <dt class="info__label">{{ $t('policyTile.statusLabel') }}</dt>
                <dd class="info__value">
                  <span :class="statusClassesForPolicy(resolvedPolicy as Policy)"></span
                  >{{
                    resolvedPolicy.policyStatus
                      ? $t('shurPolicyTile.policyStatus.' + resolvedPolicy.policyStatus)
                      : ''
                  }}
                </dd>
              </dl>
              <dl
                v-if="includedPolicies() && includedPolicies().length > 0"
                class="twoRowHeight"
              >
                <dt class="info__label">{{ $t('shurPolicyTile.includedPolicies') }}</dt>
                <dd class="info__value">
                  <template v-for="(item, idx) in includedPolicies()" :key="idx"
                    >{{ item }}<br
                  /></template>
                </dd>
              </dl>
              <dl v-if="isDetailHeader">
                <dt class="info__label">
                  {{ $t('shurPolicyTile.beginningDatePolicy') }}
                </dt>
                <dd class="info__value">
                  {{ formatDate(resolvedPolicy.beginningDatePolicy ?? '') }}
                </dd>
              </dl>
              <dl v-if="isDetailHeader && showExpiryDate()">
                <dt class="info__label">
                  {{ $t(getExpiryDateLabel()) }}
                </dt>
                <dd class="info__value">
                  {{ formatDate(resolvedPolicy.expiryDatePolicy ?? '') }}
                </dd>
              </dl>
            </template>
          </GridContent>
        </template>

        <!-- FOOTER - Header/Details Tile-->
        <template v-if="isDetailHeader" #footer>
          <GridContent :with-padding="false">
            <template #gridContent>
              <a
                v-for="(link, idx) in filterHiddenServiceLinks(specificServiceLinks)"
                :id="'policy.' + link.name + resolvedPolicy.policyNumber + idx"
                :key="idx"
                ref="serviceLinks"
                :data-track-id="link.name"
                class="service-link"
                rel="noopener noreferrer"
                tabindex="0"
                target="_blank"
                @click.prevent="goToLink(sanitizeUrl(link.href), link.name)"
              >
                {{ $t('shurPolicyTile.serviceLink.' + link.name) }}
              </a>
            </template>
          </GridContent>
        </template>
        <!-- FOOTER - Overview Tile-->
        <template v-else-if="!hasRole('helpdesk')" #footer>
          <div class="footer-cta footer-cta--left">
            <CustomButton
              :id="'policy.details-button.' + resolvedPolicy.policyNumber"
              :buttonColor="ButtonColor.SECONDARY"
              @click="goToDetailView(policy)"
            >
              {{ $t('shurPolicyTile.buttonText') }}
            </CustomButton>
          </div>
        </template>
      </Card>
    </span>
  </div>
</template>

<script lang="ts" setup>
import { computed, onMounted, type PropType, type Ref, ref } from 'vue'
import {
  formatDate,
  goToDetailView,
  mapContractBase,
  statusClassesForPolicy
} from '@/utils/policy/PolicyUtils'
import {
  ButtonColor,
  Card,
  CustomButton,
  GridContent,
  type PolicyWithIconAndTitles
} from 'de-zurich-ui-library'
import type { Policy } from 'policies-api'
import { navigate } from '@/utils/NavigationUtils'
import type { ServiceLink, ServiceLinkContext } from '@/types/policy/PolicyTypes'
import ErrorTile from '@/components/common/ErrorTile.vue'
import { useShurStore } from '@/stores/shurStore'
import { type InsuredCoverage, type ShurContract } from 'shur-api'
import { router } from '@/router/router'
import {
  shurFirstTitle,
  shurIconStringResolved,
  shurSecondTitle
} from '@/utils/shur/ShurPolicyTile.ScreenBindingsTitleIcon'
import {
  filterHiddenServiceLinks,
  resolveServiceLinksFromRules,
  sanitizeHTML
} from '@/utils/utils'
import { sanitizeUrl } from '@braintree/sanitize-url'
import { hasRole } from '@/utils/KeycloakUtils'
import { isTrue } from '@/utils/CommonUtils'
import { getServiceLinkRules } from '@/utilsTenant/serviceLinkFilterRules'

// const includedPolicies: Ref<Policy[] | PolicyWithIconAndTitles[] | null> = ref(null)
const loading = ref(true)
const resolvedPolicy: Ref<Policy | PolicyWithIconAndTitles | null> = ref(null)
const routePath = router.currentRoute.value.path
const showErrorTile = ref(false)
const shurContract: Ref<ShurContract | null> = ref(null)
const specificServiceLinks: Ref<ServiceLink[]> = ref([])
const shouldShowPolicy = ref(true)

const props = defineProps({
  isDetailHeader: {
    type: Boolean,
    default: false
  },
  policy: {
    type: Object as PropType<Policy>,
    required: true
  }
})

const secondTitleSanitizer = computed(() => {
  const t_secondTitle = shurSecondTitle(shurContract.value)
  return sanitizeHTML(t_secondTitle)
})

const productSubLine = computed(() => {
  return (shurContract.value as any)?.productSubLine as string | undefined
})

onMounted(async () => {
  resolvedPolicy.value = props.policy
  let shurContractFetched = null

  try {
    shurContractFetched = await useShurStore().getShurContractByPolicyId(
      props.policy.policyId
    )
  } catch (e) {
    console.error('ShurPolicyTile', JSON.stringify(e))
  }

  if (!shurContractFetched) {
    loading.value = false
    showErrorTile.value = true
    return
  }

  if (
    shurContractFetched.productSubType === 'WEDDING' &&
    !isTrue(globalThis.ENV_CONFIG?.FEATURE_FLAGS.SHUR.ACTIVE_TR_SECTIONS.WEDDING ?? false)
  ) {
    shurContractFetched = null
    resolvedPolicy.value = null
    shouldShowPolicy.value = false
    loading.value = false
    return
  }

  shurContract.value = shurContractFetched
  if (resolvedPolicy.value == null) {
    resolvedPolicy.value = {} as Policy
    if (shurContract.value?.contractBase)
      mapContractBase(shurContract.value.contractBase as any, resolvedPolicy.value)
  }

  // Regelbasierte Auflösung der Service-Links über tenant-spezifische Filterregeln.
  // Für SHUR gibt es kein WKZ → wkz bleibt undefined, der WKZ-Check wird übersprungen.
  const context: ServiceLinkContext = {
    ddsLines: props.policy.ddsLines ?? [],
    isRedCoop: false, // SHUR kennt kein RedCoop-Konzept
    policyStatus: resolvedPolicy.value?.policyStatus ?? '',
    wkz: undefined,
    expiryDatePolicy: resolvedPolicy.value?.expiryDatePolicy ?? undefined
  }
  specificServiceLinks.value = resolveServiceLinksFromRules(
    getServiceLinkRules(),
    context
  )

  loading.value = false
})

function goToLink(url: string, name: string): void {
  if (url) {
    let targetUrl = url
    if (name === 'contractTermination') {
      targetUrl = routePath || globalThis.location.href
      if (!targetUrl.endsWith('/')) {
        targetUrl += '/'
      }
      targetUrl += url
    }
    navigate(targetUrl)
  }
}

function includedPolicies(): string[] {
  if (
    !shurContract.value ||
    !shurContract.value.contractBundle ||
    !shurContract.value.insuredCoverageList
  )
    return []
  // get full list of subcontracts
  return (
    shurContract.value.insuredCoverageList
      // extract only product names
      .map((insuredCoverageElement: InsuredCoverage) => {
        return shurFirstTitle(insuredCoverageElement)
      })
      // remove duplicates
      .filter(function (item, pos, self) {
        return self.indexOf(item) == pos
      })
  )
}

function showExpiryDate(): boolean {
  return (
    shurContract.value?.productSubType === 'TRAVEL_COST' ||
    shurContract.value?.productSubType === 'LIABILITY_BUILDERS'
  )
}

function getExpiryDateLabel(): string {
  return shurContract.value?.productSubType === 'TRAVEL_COST'
    ? 'shurPolicyTile.expiryDatePolicy'
    : 'shurPolicyTile.endDatePolicy'
}
</script>

<style scoped>
.twoRowHeight {
  grid-row: span 2;
}

.service-link:focus-visible {
  outline: 2px solid var(--primary-color-0);
  outline-offset: 5px;
}
</style>
